import { useCallback, useEffect, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { permissionState } from '../lib/notifications';
import './NotificationPermissionBanner.css';

// ─────────────────────────────────────────────────────────────────────────────
// "Notifications are off." Shown on every page, on every device, until they
// are on.
//
// ⚠️ THIS IS NOT THE BROWSER'S PROMPT AND MUST NOT TRY TO BE. Chrome shows the
// native permission dialog ONCE per origin, ever. If the user dismisses or
// blocks it, `requestPermission()` resolves to 'denied' immediately and no
// dialog appears again — there is no second ask, no matter how many times it is
// called. So a reminder that "asks again" is a reminder that silently does
// nothing. This is an in-app strip that asks the user to act, and only calls
// the real prompt when the browser will actually show it (`default`).
//
// ⚠️ WHICH IS WHY 'denied' GETS INSTRUCTIONS INSTEAD OF A BUTTON. From that
// state the only route back is the padlock in the address bar, which nobody
// finds unaided. A button there would be a button that does nothing.
//
// ⚠️ AND WHY iOS IS ITS OWN CASE. Safari on iPhone exposes no Notification API
// at all until the site has been added to the Home Screen (iOS 16.4+), so the
// permission state reads 'unsupported' and the fix is an install, not a grant.
// Telling an iPhone user to "click the padlock" would be advice for a control
// their browser does not have.
// ─────────────────────────────────────────────────────────────────────────────

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Already installed to the Home Screen / dock, in either spelling.
const isInstalled = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
};

function NotificationPermissionBanner() {
  const { enableDesktop } = useNotifications();
  const [perm, setPerm] = useState(permissionState);
  const [busy, setBusy] = useState(false);

  // ⚠️ RE-READ ON RETURN TO THE TAB, not only on mount. Somebody who follows
  // the instructions below changes the setting in a browser panel, not in this
  // page — so without this the banner would still be sitting there when they
  // come back, telling them to do the thing they just did, and they would
  // reasonably conclude it is broken.
  const recheck = useCallback(() => setPerm(permissionState()), []);

  useEffect(() => {
    let sub;
    // The Permissions API reports the change the moment it happens, which is
    // better than waiting for a focus event. Not available everywhere, hence
    // the listeners below as well — both paths are idempotent.
    navigator.permissions?.query?.({ name: 'notifications' })
      .then((status) => {
        sub = status;
        status.onchange = recheck;
      })
      .catch(() => {});

    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      if (sub) sub.onchange = null;
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [recheck]);

  const enable = async () => {
    setBusy(true);
    try {
      // One gesture, both steps: the permission AND the push subscription that
      // lets the service worker be woken with every tab closed.
      setPerm(await enableDesktop());
    } finally {
      setBusy(false);
    }
  };

  // The whole point: it disappears the moment it is no longer true.
  if (perm === 'granted') return null;

  // An iPhone that has not installed the app yet. Actionable, so it is shown.
  if (perm === 'unsupported' && isIos() && !isInstalled()) {
    return (
      <div className="npb npb-info" role="status">
        <span className="npb-icon" aria-hidden="true">🔔</span>
        <div className="npb-text">
          <strong>Alerts are off on this iPhone.</strong>{' '}
          Tap <strong>Share</strong> then <strong>Add to Home Screen</strong>, and open the
          app from there — iOS only allows notifications for installed apps.
        </div>
      </div>
    );
  }

  // Nothing the user can do: an insecure origin, or a browser without the API.
  // Nagging here would be pure noise, so it stays silent — the in-app bell
  // still works perfectly.
  if (perm === 'unsupported') return null;

  if (perm === 'denied') {
    return (
      <div className="npb npb-warn" role="status">
        <span className="npb-icon" aria-hidden="true">🔕</span>
        <div className="npb-text">
          <strong>Notifications are blocked for this site.</strong>{' '}
          {isIos()
            ? 'Open Settings → Notifications → find this app → Allow Notifications.'
            : 'Click the padlock (or ⓘ) beside the address bar → Notifications → Allow, then reload.'}
          {' '}Expiry reminders and task alerts cannot reach you until this is on.
        </div>
      </div>
    );
  }

  // 'default' — the browser will genuinely show its prompt, so offer the button.
  return (
    <div className="npb npb-warn" role="status">
      <span className="npb-icon" aria-hidden="true">🔔</span>
      <div className="npb-text">
        <strong>Notifications are off.</strong>{' '}
        Turn them on to be told about expiring DSCs, due filings and new tasks
        even when this tab is closed.
      </div>
      <button type="button" className="npb-btn" onClick={enable} disabled={busy}>
        {busy ? 'Enabling…' : 'Turn on notifications'}
      </button>
    </div>
  );
}

export default NotificationPermissionBanner;
